"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FirebaseRepo = void 0;
const tslib_1 = require("tslib");
const GenericMap_1 = require("../../common/mappers/GenericMap");
const firebaseTypes_1 = require("./firebaseTypes");
const utils_1 = require("./utils");
const json2csv_1 = require("json2csv");
const debuggers_1 = require("../utils/debuggers");
const lodash_1 = require("lodash");
const DatabaseRefs_1 = tslib_1.__importDefault(require("../../common/domain/structures/DatabaseRefs"));
const classDbg = debuggers_1.extendDebug(debuggers_1.getExtendedBaseDebug("FirebaseRepo"));
const VALIDATE_DOMAIN_CLASSES = false;
class FirebaseRepo {
    constructor(app, ctx) {
        this.rootRefs = DatabaseRefs_1.default.rootRefs;
        this.app = app;
        this.db = app.db;
        this.ouRefs = DatabaseRefs_1.default.builder.byOrgIdUserIdAll(ctx || app.claims);
        this.accountId = (ctx && ctx.accountId) || app.claims.accountId;
        this.orgId = (ctx && ctx.orgId) || app.claims.orgId;
        this.userId = (ctx && ctx.userId) || app.claims.userId;
    }
    async fetchArray(p) {
        const dbg = classDbg("fetchArray");
        return utils_1.rslv((async function (db) {
            if (!p.validateDTO && !p.toDomain)
                throw Error(`Param object needs to contain toDomain or validateDTO ${JSON.stringify(p)}`);
            const dbBaseRef = db.ref(p.ref);
            const query = FirebaseRepo.extendRef(dbBaseRef, p.order, p.filter);
            const snap = await query.once("value");
            const res = { ok: [], fail: [] };
            if (!snap.exists()) {
                const msg = "No result";
                dbg.vrbs(`No result: %s %s %s`, snap.ref.ref, snap.key, p.matcher && p.matcher.pattern);
                throw Error(msg);
            }
            if (!snap.hasChildren()) {
                const msg = "No children. Contains primitive or null";
                dbg.vrbs(`No result: %s %s %s`, snap.ref.ref, snap.key, p.matcher && p.matcher.pattern);
                throw Error(msg);
            }
            snap.forEach((item) => {
                if (p.matcher && p.matcher(item.val())) {
                    return;
                }
                if (p.toDomain) {
                    const domainObject = p.toDomain(item.val(), p.ref, item.key, VALIDATE_DOMAIN_CLASSES);
                    if (domainObject)
                        res.ok.push(domainObject);
                    else
                        res.fail.push({ ref: p.ref, error: "Mapping failed" });
                    return;
                }
                if (p.validateDTO) {
                    const valStatus = p.validateDTO(item.val(), p.ref, item.key);
                    if (valStatus.pass)
                        res.ok.push(item.val());
                    else
                        res.fail.push({ ref: p.ref, error: valStatus.msg });
                    return;
                }
            });
            return res;
        })(this.db));
    }
    async fetchItem(p) {
        const dbg = classDbg("fetchItem");
        return utils_1.rslv((async function (db) {
            const dbBaseRef = db.ref(p.ref + "/" + p.key);
            const query = FirebaseRepo.extendRef(dbBaseRef, p.order, p.filter);
            const snap = await query.once("value");
            if (!snap.exists()) {
                const msg = "No result";
                dbg.vrbs(`No result: %s %s %s`, snap.ref.ref, snap.key, p.matcher && p.matcher.pattern);
                throw Error(msg);
            }
            if (p.matcher && p.matcher(snap.val())) {
                const msg = "Record not matched";
                dbg.vrbs(`No result: %s %s %s`, snap.ref.ref, snap.key, p.matcher && p.matcher.pattern);
                throw Error(msg);
            }
            if (p.toDomain) {
                return p.key
                    ? p.toDomain(snap.val(), p.ref, p.key, VALIDATE_DOMAIN_CLASSES)
                    : p.toDomain(snap.val(), p.ref, VALIDATE_DOMAIN_CLASSES);
            }
            if (p.validateDTO) {
                return p.validateDTO(snap.val(), p.ref, snap.key).pass ? snap.val() : null;
            }
            throw Error(`Param object needs to contain toDomain or validateDTO ${JSON.stringify(p)}`);
        })(this.db));
    }
    async removeItem(baseRef, key, callback) {
        return utils_1.rslv(this.db.ref(baseRef).child(key).remove(callback));
    }
    async setRemoved(baseRef, key, removedBy, appType, comment, callback) {
        // const removedAt = Timestamp.create(ADMIN_SV_TIMESTAMP);
        // const removed: Removed = Removed.create({ removedBy, removedAt, appType, comment });
        // const removedDTO = GenericMap.toDTO(removed);
        const removedAt = firebaseTypes_1.FB_SV_TIMESTAMP;
        const removedDTO = { removedBy, removedAt, appType: appType.value, comment };
        return utils_1.rslv(this.db.ref(baseRef).child(key).update({ removed: removedDTO }, callback));
    }
    /**
     * Warning, only applicable if dto and domain props have same names
     *
     */
    async updateEntity(entity, callback) {
        const key = entity.isNew ? this.db.ref(entity.ref).push().key : entity.id.toValue();
        const dto = GenericMap_1.GenericMap.toDTO(entity);
        const flatForUpdate = utils_1.flat(dto);
        FirebaseRepo.removeUndefinedPropsFromObj(flatForUpdate);
        return utils_1.rslv(this.db.ref(entity.ref).child(key).update(flatForUpdate, callback));
    }
    async updateItem(baseRef, existingKey, object, callback) {
        const key = existingKey ? existingKey : this.db.ref(baseRef).push().key;
        const flatForUpdate = utils_1.flat(object);
        FirebaseRepo.removeUndefinedPropsFromObj(flatForUpdate);
        return utils_1.rslv(this.db.ref(baseRef).child(key).update(flatForUpdate, callback));
    }
    async updateBatch(baseRef, keyValueObjects, callback) {
        const batch = {};
        for (const item of keyValueObjects) {
            if (!item.key && !item.value)
                continue;
            const key = item.key ? item.key : this.db.ref(baseRef).push().key;
            const val = typeof item.value === "object" ? GenericMap_1.GenericMap.toDTO(item.value) : item.value || null;
            batch[key] = val;
        }
        const flatBatch = utils_1.flat(batch);
        FirebaseRepo.removeUndefinedPropsFromObj(flatBatch);
        return utils_1.rslv(this.db.ref(baseRef).update(flatBatch, callback));
    }
    static extendRef(ref, order, filter) {
        if (order && (order.child || order.key || order.value)) {
            let query;
            if (order.child) {
                query = ref.orderByChild(order.child);
            }
            else if (order.key) {
                query = ref.orderByKey();
            }
            else if (order.value) {
                query = ref.orderByValue();
            }
            if (filter) {
                if ("equalTo" in filter) {
                    query = query.equalTo(filter.equalTo);
                }
                else {
                    if ("startAt" in filter)
                        query = query.startAt(filter.startAt);
                    if ("endAt" in filter)
                        query = query.endAt(filter.endAt);
                }
                if ("limitToFirst" in filter) {
                    query = query.limitToFirst(filter.limitToFirst);
                }
                else if ("limitToLast" in filter) {
                    query = query.limitToLast(filter.limitToLast);
                }
            }
            return query || ref;
        }
        return ref;
    }
    static toCSV(dto, opts) {
        return json2csv_1.parse(dto, opts);
    }
    static addMappedSnapshot(res, snap, { toDomain, validateDTO, matcher }) {
        snap.forEach((item) => {
            if (matcher && matcher(item.val())) {
                //TODO test
                return;
            }
            if (toDomain) {
                const domainObject = toDomain(item.val(), FirebaseRepo.refFromSnap(snap), item.key, VALIDATE_DOMAIN_CLASSES);
                if (domainObject)
                    res.ok.push(domainObject);
                else
                    res.fail.push({ ref: FirebaseRepo.refFromSnap(snap), error: "Mapping failed" });
                return;
            }
            if (validateDTO) {
                const valStatus = validateDTO(item.val(), FirebaseRepo.refFromSnap(snap.ref), item.key);
                if (valStatus.pass)
                    res.ok.push(item.val());
                else
                    res.fail.push({ ref: FirebaseRepo.refFromSnap(snap), error: valStatus.msg });
                return;
            }
        });
    }
    async mapSnapshotWithChildren({ snap, toDomain, validateDTO, matcher, }) {
        return utils_1.rslv((async () => {
            const res = { ok: [], fail: [] };
            if (!snap.hasChildren()) {
                return Promise.reject("Snapshot has no children");
            }
            FirebaseRepo.addMappedSnapshot(res, snap, { toDomain, validateDTO, matcher });
            return res;
        })());
    }
    static removeUndefinedPropsFromObj(obj) {
        if (!lodash_1.isObject(obj))
            return;
        for (const key of Object.keys(obj)) {
            if (typeof obj[key] == "undefined")
                delete obj[key];
        }
    }
    static refFromSnap(snap) {
        return snap.ref.toString();
    }
}
exports.FirebaseRepo = FirebaseRepo;
//# sourceMappingURL=FirebaseRepo.js.map